import { DropdownTheme, Dropdown as TPADropdown } from '@wix/wix-ui-tpa/cssVars';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useConfig } from '../../contexts/config-context';
import { useFocusFieldEvent } from '../../contexts/events-context';
import { ForcedState, useForcedState } from '../../contexts/forced-state-context';
import { useFocus } from '../../hooks/use-focus';
import { useFieldAttributes } from '../../sdk/components';
import { FieldDescriptionWUT, FieldErrorWUT, FieldLabelWUT } from '../../sdk/components-wut';
import { classes } from './dropdown-field-wut.st.css';
const getOptionId = (fieldId, index)=>`${fieldId}-opt-${index}`;
export const DropdownFieldWUT = ({ id, isLoading, target, value, defaultValue, error, onChange, onFocus, onBlur, options = [], required = false, label, hideLabel = false, description, placeholder, disabled = false, readOnly, hasError = false, errorMessage, className, FieldLayout, requiredIndicatorType, requiredIndicatorPlacement })=>{
    const { isMobile } = useConfig();
    const valueByLabel = useMemo(()=>options.reduce((result, option)=>{
            result[option.label] = option.value;
            return result;
        }, {}), [
        options
    ]);
    const selectableOptions = useMemo(()=>options.map((option, index)=>{
            return {
                id: getOptionId(id, index),
                value: option.label,
                isSelectable: true
            };
        }), [
        options,
        id
    ]);
    const idByValue = useMemo(()=>options.reduce((result, option, index)=>{
            result[option.value] = getOptionId(id, index);
            return result;
        }, {}), [
        id,
        options
    ]);
    const forcedHover = useForcedState().includes(ForcedState.INPUT_FIELDS_HOVER);
    const { labelId, errorId, descriptionId, ariaDescribedBy } = useFieldAttributes(id, error, description);
    const dropdownRef = useRef(null);
    useFocusFieldEvent(()=>{
        dropdownRef.current?.focus({
            preventScroll: true
        });
    }, id);
    const { handleBlur, containerRef } = useFocus({
        onBlur
    });
    // Selecting an option changes `value`, which changes the `key` below and
    // forces the whole dropdown (including the currently focused trigger) to
    // unmount and remount. The fresh trigger isn't focused, so focus drops to
    // <body> and the next Tab restarts from the top of the page. Re-focus the
    // dropdown after the remount, but only for a user selection (not for external
    // value changes) so we never steal focus from elsewhere on the page.
    const shouldRefocusRef = useRef(false);
    const handleChange = useCallback((option)=>{
        const optionValue = option.value ? valueByLabel[option.value] : '';
        // Only refocus on a real value change, so a stale flag can't later steal focus.
        shouldRefocusRef.current = optionValue !== value;
        onChange(optionValue);
    }, [
        valueByLabel,
        onChange,
        value
    ]);
    useEffect(()=>{
        if (shouldRefocusRef.current) {
            shouldRefocusRef.current = false;
            dropdownRef.current?.focus({
                preventScroll: true
            });
        }
    }, [
        value
    ]);
    // TODO: contribution to WUT needed. Passing undefined as initialSelectedId does not rerender dropdown.
    const initialSelectedId = useMemo(()=>value && idByValue[value] || defaultValue && idByValue[defaultValue] || String(Math.random() * Date.now()), [
        defaultValue,
        value,
        idByValue
    ]);
    return /*#__PURE__*/ React.createElement(FieldLayout, {
        fieldId: id,
        renderLabel: ()=>/*#__PURE__*/ React.createElement(FieldLabelWUT, {
                labelId: labelId,
                // TODO: use id & htmlFor instead of aria-labelledby
                // htmlFor={inputId}
                required: required,
                label: label,
                hideLabel: hideLabel,
                requiredIndicatorType: requiredIndicatorType,
                requiredIndicatorPlacement: requiredIndicatorPlacement
            }),
        renderInput: ()=>/*#__PURE__*/ React.createElement("div", {
                onFocus: onFocus,
                onBlur: handleBlur,
                ref: containerRef,
                "aria-label": required ? 'required' : ''
            }, /*#__PURE__*/ React.createElement(TPADropdown, {
                // TODO: use id & htmlFor instead of aria-labelledby
                // id={inputId}
                isLoading: isLoading,
                "aria-labelledby": labelId,
                "data-hook": `dropdown-${id}`,
                appendTo: "parent",
                error: hasError,
                newErrorMessage: true,
                className: classNames(classes.root, classes.theme, {
                    [classes.error]: hasError,
                    [classes.hover]: forcedHover,
                    [classes.mobileDropdown]: isMobile
                }, className),
                placeholder: placeholder,
                disabled: disabled,
                readOnly: readOnly,
                name: label,
                options: selectableOptions,
                key: `${id}-${value}`,
                mobileNativeSelect: isMobile,
                initialSelectedId: initialSelectedId,
                onChange: handleChange,
                upgrade: true,
                "aria-label": label,
                theme: DropdownTheme.Box,
                "aria-describedby": ariaDescribedBy,
                "aria-invalid": hasError,
                ref: dropdownRef,
                flip: false
            })),
        renderDescription: ()=>/*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(FieldErrorWUT, {
                target: target,
                hasError: hasError,
                errorMessage: errorMessage,
                id: errorId
            }), /*#__PURE__*/ React.createElement(FieldDescriptionWUT, {
                description: description,
                id: descriptionId
            }))
    });
};

//# sourceMappingURL=dropdown-field-wut.js.map